import type { InputHTMLAttributes, JSX } from "react";
import { typography } from "../Typography/styles";
import {
  FieldErrorMessage,
  RequiredMark,
  fieldLabelClassName,
  fieldFocusCn,
} from "./fieldHelpers";
import { cn } from "packages/dsaui/src/utils";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  errorMessage?: string | null;
  subtitle?: string;
}

export const Input = ({
  id,
  label,
  placeholder,
  errorMessage,
  required,
  subtitle,
  ...rest
}: InputProps): JSX.Element => {
  const { disabled, name } = rest;
  const messageId = id ?? name;
  const helpText = errorMessage ?? subtitle ?? undefined;

  return (
    <div className="mb-4 flex w-full flex-col items-start gap-1 text-left">
      {label && (
        <label htmlFor={id} className={fieldLabelClassName}>
          {label}
          <RequiredMark required={required} />
        </label>
      )}
      <input
        {...rest}
        id={id}
        aria-label={`${name}-input-container`}
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={errorMessage ? "true" : undefined}
        aria-describedby={helpText ? `${messageId}-message` : undefined}
        className={cn(
          "w-full rounded-[6px] border border-gray-300 px-[13px] py-[9px]",
          typography.formInput.className,
          "shadow-sm",
          fieldFocusCn(errorMessage),
          disabled ? "cursor-not-allowed bg-gray-50" : "bg-white",
        )}
      />
      {(errorMessage && (
        <FieldErrorMessage
          id={`${messageId}-message`}
          ariaLabel={`${name}-input-error`}
        >
          {errorMessage}
        </FieldErrorMessage>
      )) ||
        (subtitle && (
          <p
            id={`${messageId}-message`}
            aria-label={`${name}-input-subtitle`}
            className={typography.formInputSubtitle.className}
          >
            {subtitle}
          </p>
        ))}
    </div>
  );
};

export default Input;
